import { InvestmentTransactionModals, TxResult } from "@/constants/enums";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FramerButton, ServiceProviderDetails } from "@/components/shared";
import { formatNumberLocale } from "@/utils/helpers";
import { handleBlockchainExplorerLink } from "@/blockchain/helpers";
import { useTranslation } from "react-i18next";
import { useCompanyData } from "@/hooks";
import { useIntl } from "react-intl";
import { propsInvestmentTransactionResultModal } from "../props";

type Props = propsInvestmentTransactionResultModal & {
  showModal: InvestmentTransactionModals;
};

function InvestmentTransactionResultModal(props: Props) {

  // Props
  const { showModal, assetFinancingPool, transactionHash, state, transactionResultType, setShowModal } = props;

  // Hooks
  const intl = useIntl();
  const { t } = useTranslation(["modals"]);
  const company = useCompanyData();
  // Constants
  const isError = TxResult.ERROR === transactionResultType;

  const transactionDetailsError = [
    {
      label: t("investmenttransactionresult.transactiondetails.txamount"),
      content: formatNumberLocale(intl, state.amount, "currency")
    },
    {
      label: t("investmenttransactionresult.transactiondetails.fromaddress"),
      content: company?.publickey
    },
    {
      label: t("investmenttransactionresult.transactiondetails.toaddress"),
      content: assetFinancingPool!.pooladdress
    }
  ];

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setShowModal(InvestmentTransactionModals.NoModal);
    }
  };

  return (
    <Dialog open={showModal === InvestmentTransactionModals.InvestmentTransactionResult} onOpenChange={handleOpenChange}>
      <DialogContent className="px-6 max-w-3xl max-h-[90vh] overflow-y-auto dark:bg-mfdarklight"
        onPointerDownOutside={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Investment Transaction Result</DialogTitle>
        </DialogHeader>

        <div className="flex-1 pt-2 overflow-y-auto">
          {/* Heading */}
          <div className={`text-lg font-bold text-center ${isError ? "text-red-500" : "text-green-500"}`}>
            {isError ? t("investmenttransactionresult.fail.heading") : t("investmenttransactionresult.success.heading")}
          </div>

          {/* Image / animation */}
          <div className="mb-6">
            {!isError && (
              <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 130.2 130.2" className="w-28 h-28 mx-auto mt-2 text-green-500">
                <circle className="animated-icon-path animated-icon-circle" fill="none" stroke="currentColor" strokeWidth="6" strokeMiterlimit="10" cx="65.1" cy="65.1" r="62.1" />
                <polyline className="animated-icon-path animated-icon-check" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeMiterlimit="10" points="100.2,40.2 51.5,88.8 29.8,67.5 " />
              </svg>
            )}
            {isError && (
              <svg version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 130.2 130.2" className="w-28 h-28 mx-auto mt-2 text-red-500">
                <circle className="animated-icon-path animated-icon-circle" fill="none" stroke="currentColor" strokeWidth="6" strokeMiterlimit="10" cx="65.1" cy="65.1" r="62.1" />
                <line className="animated-icon-path animated-icon-line" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeMiterlimit="10" x1="34.4" y1="37.9" x2="95.8" y2="92.3" />
                <line className="animated-icon-path animated-icon-line" fill="none" stroke="currentColor" strokeWidth="6" strokeLinecap="round" strokeMiterlimit="10" x1="95.8" y1="38" x2="34.4" y2="92.2" />
              </svg>
            )}
          </div>

          {/* Subtext */}
          <div className="mb-4">
            <p className="dark:text-white">
              {transactionResultType === "error" ? t("investmenttransactionresult.fail.text"):
                (
                  <span>
                    {t("investmenttransactionresult.success.textbefore")}
                    <span className="pl-1 font-semibold">
                      {formatNumberLocale(intl, props.state.amount, "currency")}
                    </span> {t("investmenttransactionresult.success.textafter")}:
                  </span>
                )}
            </p>
          </div>

          {/* SP Details */}
          <ServiceProviderDetails
            serviceProviderId={assetFinancingPool!.spdetails._id}
          />

          {/* Transaction Details */}
          <div className="flex flex-col gap-2 mt-4 mb-8">
            <h2 className="font-semibold dark:text-white">{t("investmenttransactionresult.transactiondetails.title")}</h2>
            {
              isError &&
                <>
                  {
                    transactionDetailsError.map((item) => (
                      <div key={`companydetails:${item.label}`}>
                        <p className="text-xs text-gray-400 mb-1 dark:text-gray-300">{item.label}</p>
                        <p className="text-xs dark:text-white">{item.content}</p>
                      </div>
                    ))
                  }
                </>
            }
            {
              transactionHash !== "" &&
              <div>
                <p className="text-xs text-gray-400 mb-1 dark:text-gray-300">{t("investmenttransactionresult.transactiondetails.txhash")}</p>
                <p className="text-xs dark:text-white break-all">{transactionHash}</p>
              </div>
            }
          </div>

          {/* Disclaimer */}
          <div className="mb-8 dark:text-white">
            {
              transactionHash !== "" &&
                <p className="text-xs">
                  {t("investmenttransactionresult.notes.viewtx")}
                  <a href={handleBlockchainExplorerLink("tx", transactionHash)} rel="noreferrer" target="_blank" className="text-blue-400"> Explorer</a>
                </p>
            }
            {
              isError &&
                <>
                  <p className="text-xs my-2">{t("investmenttransactionresult.notes.errormsg")}</p>
                  <p className="text-xs">{t("investmenttransactionresult.notes.contactsupport")} <a href="mailto:contact@myron.finance" className="text-blue-400">contact@myron.finance</a></p>
                </>
            }
          </div>
        </div>

        <DialogFooter>
          {/* Buttons */}
          <div className={`flex items-center w-full ${!isError ? "justify-center" : "justify-between"}`}>
            {!isError && (
              <FramerButton
                onClick={() => {
                  setShowModal(InvestmentTransactionModals.NoModal);
                }}
                className="bg-mforange text-white hover:bg-mforange/80"
              >
                {t("common:buttons.close")}
              </FramerButton>
            )}
            {isError && (
              <>
                <FramerButton
                  onClick={() => {
                    setShowModal(InvestmentTransactionModals.NoModal);
                  }}
                  variant="outline-primary"
                >
                  {t("common:buttons.cancel")}
                </FramerButton>
                <FramerButton
                  onClick={() => {
                    setShowModal(InvestmentTransactionModals.MakeInvestment);
                  }}
                  className="bg-mforange text-white hover:bg-mforange/80"
                >
                  {t("common:buttons.retry")}
                </FramerButton>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default InvestmentTransactionResultModal;
